/* eslint-disable no-undef */
import React from 'react'

/** Material UI **/
import { alpha, styled } from '@mui/material/styles'
import { makeStyles, Tooltip, Button } from '@material-ui/core'
import InputLabel from '@mui/material/InputLabel'
import FormControl from '@mui/material/FormControl'
import customTheme from '../../styles/mui_theme'
import { AutoCompleteAddress } from '../accountsettings/AutoCompleteAddress'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCircleInfo } from '@fortawesome/pro-regular-svg-icons'
import { useTranslation } from 'react-i18next'

const BootstrapInput = styled(AutoCompleteAddress)(({ theme }) => ({
  'label + &': {
    marginTop: theme.spacing(3),
    color: customTheme.colors.profile.text_grey,
    borderRadius: 45,
    position: 'relative',
    backgroundColor: customTheme.colors.white,
    border: '1px solid ' + customTheme.colors.profile.border_input,
    fontSize: 12,
    width: '100%',
    padding: '10px 12px',
    transition: theme.transitions.create([
      'border-color',
      'background-color',
      'box-shadow'
    ]),
    fontFamily: [
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
      '"Apple Color Emoji"',
      '"Segoe UI Emoji"',
      '"Segoe UI Symbol"'
    ].join(','),
    '&:focus': {
      boxShadow: `${alpha(theme.palette.primary.main, 0.25)} 0 0 0 0.2rem`,
      borderColor: theme.palette.primary.main
    },
    '&:disabled': {
      backgroundColor: customTheme.colors.disable_background,
      cursor: 'not-allowed',
      color: customTheme.colors.disable_text
    }
  }
}))

const useStyles = makeStyles(theme => ({
  bottomSpacing: {
    marginBottom: 10
  },
  label: {
    color: customTheme.colors.grey,
    fontSize: '12px',
    marginBottom: '0.5em',
    textAlign: 'left',
    marginLeft: '5px'
  },
  required: {
    color: theme.colors.errorText
  },
  highlightField: {
    borderColor: theme.colors.errorText + '!important'
  },
  infoIconWrapper: {
    display: 'inline-flex',
    alignItems: 'center',
    cursor: 'pointer',
    padding: '2px',
    height: '20px',
    marginLeft: '8px'
  },
  infoIcon: {
    color: theme.colors.icon_orange,
    fontSize: '22px',
    pointerEvents: 'none'
  },
  tooltip: {
    backgroundColor: theme.colors.dark_blue,
    color: theme.colors.white,
    fontSize: '12px',
    lineHeight: '19px',
    padding: '8px',
    maxWidth: '400px',
    '& .MuiTooltip-arrow': {
      color: theme.colors.dark_blue
    }
  },
  labelWithTooltip: {
    color: customTheme.colors.grey,
    fontSize: '12px',
    marginBottom: '0.5em',
    textAlign: 'left',
    marginLeft: '5px',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    '& > span': {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '4px'
    }
  },
  requestChangeButton: {
    position: 'absolute',
    top: '25px',
    right: '0',
    backgroundColor: theme.palette.primary.main,
    color: '#fff',
    textTransform: 'none',
    borderRadius: '45px',
    padding: '8px 24px',
    fontSize: '12px',
    width: '140px',
    height: '40px',
    whiteSpace: 'nowrap',
    '&:hover': {
      backgroundColor: alpha(theme.palette.primary.main, 0.9)
    }
  },
  inputWithButton: {
    width: 'calc(100% - 148px) !important'
  }
}))

export default function GlobalAddressInput(props) {
  const classes = useStyles()
  const { t } = useTranslation()

  const handleChange = (event, field, addressInfo) => {
    props.onChange(event.target.value, field, addressInfo)
  }
  const renderInfoIcon = () => {
    if (props.showTooltip && props.tooltipText) {
      return (
        <Tooltip
          title={props.tooltipText}
          placement="right"
          arrow
          PopperProps={{
            modifiers: {
              preventOverflow: {
                enabled: true,
                boundariesElement: 'viewport'
              }
            }
          }}
          classes={{
            tooltip: classes.tooltip
          }}
        >
          <div className={classes.infoIconWrapper}>
            <FontAwesomeIcon
              icon={faCircleInfo}
              className={classes.infoIcon}
            />
          </div>
        </Tooltip>
      )
    }
    return null
  }

  const showRequestButton = props.allowRequestChange && props.showTooltip

  return (
    <FormControl variant="standard" fullWidth={true}>
      <InputLabel shrink htmlFor="bootstrap-input" className={props.showTooltip ? classes.labelWithTooltip : classes.label}>
        <span>
          {props.required && <span className={classes.required}>*</span>}
          {props.label}
          {renderInfoIcon()}
        </span>
      </InputLabel>
      <BootstrapInput
        {...props}
        onChange={handleChange}
        id="bootstrap-input"
        className={`
          ${props.required && props.value?.length === 0 ? classes.highlightField : ''}
          ${showRequestButton ? classes.inputWithButton : ''}
        `}
        disabled={props.allowRequestChange ? props.disabled : false}
      />
      {showRequestButton && (
        <Button
          variant="contained"
          className={classes.requestChangeButton}
          disableElevation
          onClick={() => props.onRequestChange?.(props.field)}
        >
          {t('company_profile.requires_approval.request_change')}
        </Button>
      )}
      <div className={classes.bottomSpacing}></div>
    </FormControl>
  )
}
